import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const getApiBaseUrl = () => {
  const envUrl = import.meta.env?.VITE_API_BASE_URL;
  if (envUrl) return envUrl;
  // Dev: same-origin — the Vite proxy forwards /api, /db and /auth to the
  // backend, which keeps any dev-server port working without CORS entries
  if (import.meta.env?.DEV) return '';
  return 'https://modlrn.onrender.com';
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// De-dupe concurrent refreshes: many 401s share one in-flight refresh call
let refreshInFlight: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  try {
    // Bare axios (not `api`) so this request skips the interceptors
    const { data } = await axios.post(
      `${getApiBaseUrl()}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    return data.access_token as string;
  } catch {
    return null;
  }
};

const clearSessionAndRedirect = async () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  const { useAuthStore } = await import('../store/authStore');
  useAuthStore.getState().setUser(null);
  const path = window.location.pathname;
  if (path !== '/login' && path !== '/signup') {
    window.location.href = '/login';
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && original && !original._retried && !isRefreshCall) {
      original._retried = true;
      refreshInFlight = refreshInFlight ?? refreshAccessToken();
      const newToken = await refreshInFlight;
      refreshInFlight = null;

      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original); // replay the original request once
      }
      await clearSessionAndRedirect();
    }
    return Promise.reject(error);
  }
);

/** The coded error envelope every endpoint now returns: `{detail: {code, message, ...}}`. */
interface ErrorDetail {
  code?: string;
  message?: string;
  retry_after?: number;
  replenishes_at?: string;
  [k: string]: unknown;
}

export interface ApiErrorInfo {
  /** Machine code to switch on (e.g. 'challenge_expired'), or null for network/unknown. */
  code: string | null;
  /** Ready-to-show message (client override → server message → per-status fallback). */
  message: string;
  /** HTTP status, or 0 when there was no response. */
  status: number;
  /** The full detail object, for reading extras like replenishes_at. */
  detail: ErrorDetail | null;
}

/**
 * Client-side copy overrides, keyed by code. The server always sends a safe
 * `message`; this map lets specific flows read nicer/branded copy without
 * matching prose. Anything not listed falls back to the server message.
 */
const errorMessages: Record<string, string> = {
  invalid_token: 'Your session has expired. Please log in again.',
  account_missing: 'This account no longer exists.',
  challenge_expired: 'This challenge has expired. Try a rematch!',
  session_expired: "Time's up — this game expired before it was submitted.",
  already_played: "You've already played your turn for this.",
  rate_limited: "You're doing that too fast. Please slow down a moment.",
  internal_error: 'Something went wrong on our end. Please try again in a moment.',
};

const statusFallback = (status: number, retryAfter?: string | number): string => {
  switch (status) {
    case 400:
      return 'That request looks invalid. Please check your input and try again.';
    case 401:
      return 'Invalid credentials. Please check your details and try again.';
    case 403:
      return "You don't have access to this.";
    case 404:
      return "We couldn't find what you were looking for.";
    case 429:
      return `Too many requests — please wait ${retryAfter || 60}s and try again.`;
    case 500:
      return 'The server hit an unexpected error. Please try again in a moment.';
    case 503:
      return 'The service is temporarily unavailable. Please try again shortly.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

/**
 * Structured view of any API error: `{code, message, status, detail}`.
 * Reads the coded `detail` object (or a legacy string detail), applies the
 * client override map, and falls back to a per-status message. Use `.code`
 * to branch (energy modal, rematch CTA); use `.message` for toasts.
 */
export const apiError = (error: unknown, fallback = 'Something went wrong'): ApiErrorInfo => {
  if (!axios.isAxiosError(error)) return { code: null, message: fallback, status: 0, detail: null };

  if (error.code === 'ECONNABORTED') {
    return {
      code: 'timeout',
      message: 'The request took too long. Please check your connection and try again.',
      status: 0,
      detail: null,
    };
  }
  if (!error.response) {
    return {
      code: 'offline',
      message: 'Unable to reach the server. It may be offline — please try again shortly.',
      status: 0,
      detail: null,
    };
  }

  const status = error.response.status;
  const raw = (error.response.data as { detail?: unknown } | undefined)?.detail;

  // New shape: detail is the coded object
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const detail = raw as ErrorDetail;
    const code = typeof detail.code === 'string' ? detail.code : null;
    const message =
      (code && errorMessages[code]) ||
      (typeof detail.message === 'string' && detail.message) ||
      statusFallback(status, detail.retry_after ?? error.response.headers['retry-after']);
    return { code, message, status, detail };
  }

  // Legacy string detail (belt-and-suspenders; the backend now always wraps)
  const text = typeof raw === 'string' && raw && raw !== 'Internal Server Error' ? raw : null;
  return {
    code: null,
    message: text || statusFallback(status, error.response.headers['retry-after']),
    status,
    detail: null,
  };
};

/** Human-readable message from an API error, for toasts and inline errors. */
export const apiErrorMessage = (error: unknown, fallback = 'Something went wrong'): string =>
  apiError(error, fallback).message;

export default api;
